import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ScrollText } from 'lucide-react';

interface TermsAcceptanceModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAccept: () => void;
  submitting: boolean;
}

export default function TermsAcceptanceModal({ isOpen, onClose, onAccept, submitting }: TermsAcceptanceModalProps) {
  const [agreed, setAgreed] = useState(false);

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center px-4 py-8"
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className="relative z-10 w-full max-w-2xl max-h-[90vh] flex flex-col glass-card border border-white/10 rounded-2xl overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-electric-cyan/10 flex items-center justify-center">
                  <ScrollText className="w-5 h-5 text-electric-cyan" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-white">Terms of Service</h2>
                  <p className="text-xs text-text-muted font-mono">v1.0.0 — May 2026</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-lg hover:bg-white/5 transition-colors"
              >
                <X className="w-5 h-5 text-text-muted" />
              </button>
            </div>

            {/* Scrollable Terms Content */}
            <div className="flex-1 overflow-y-auto px-6 py-5 terms-scroll">
              <div className="prose prose-invert prose-sm max-w-none space-y-6">
                {/* Section 1 */}
                <section>
                  <h3 className="text-base font-bold text-white flex items-center gap-2 mb-3">
                    <span className="text-electric-cyan font-mono text-xs">01.</span> The Agreement
                  </h3>
                  <p className="text-text-secondary text-sm leading-relaxed">
                    fans.audio is a trading style of Boogie Microphone Limited ("the Company"). By accessing the fans.audio platform ("the Platform"), whether as an Artist or a Fan (collectively "Users"), you enter into a legally binding agreement with Boogie Microphone Limited.
                  </p>
                </section>

                {/* Section 2 */}
                <section>
                  <h3 className="text-base font-bold text-white flex items-center gap-2 mb-3">
                    <span className="text-electric-cyan font-mono text-xs">02.</span> Artist Sovereignty & Content Verification
                  </h3>
                  <ul className="space-y-3 text-text-secondary text-sm list-none pl-0">
                    <li className="flex gap-2">
                      <span className="text-electric-cyan font-mono shrink-0">›</span>
                      <span><strong className="text-white">Ownership:</strong> Artists retain 100% ownership of all Intellectual Property uploaded to the Platform. The Company claims no ownership or licensing rights beyond the limited right to host, process, and deliver the content to authorized Fans.</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-electric-cyan font-mono shrink-0">›</span>
                      <span><strong className="text-white">The Authentic Signal:</strong> To protect the integrity of the Platform, the Company uses active intelligent processing of all tracks to flag and filter for unauthorized AI-generated content or copyright infringement. By uploading, the Artist warrants the work is an authentic creation.</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-electric-cyan font-mono shrink-0">›</span>
                      <span><strong className="text-white">Fixed Pressings:</strong> Pressings represent collectible digital releases. Once an Artist locks and publishes a Pressing, the contents become immutable to ensure the long-term value of the Fan's collection.</span>
                    </li>
                  </ul>
                </section>

                {/* Section 3 */}
                <section>
                  <h3 className="text-base font-bold text-white flex items-center gap-2 mb-3">
                    <span className="text-electric-cyan font-mono text-xs">03.</span> The Credit Economy & Access Keys
                  </h3>
                  <ul className="space-y-3 text-text-secondary text-sm list-none pl-0">
                    <li className="flex gap-2">
                      <span className="text-electric-cyan font-mono shrink-0">›</span>
                      <span><strong className="text-white">Subscription Model:</strong> Fans subscribe to the Platform for the right to use credits to unlock Artist Pressings.</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-electric-cyan font-mono shrink-0">›</span>
                      <span><strong className="text-white">The Access Key:</strong> Once a Pressing is unlocked using a credit, the Fan is granted a digital "key" to that Pressing. This key remains valid for the remaining lifetime of the Fan's active subscription.</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-electric-cyan font-mono shrink-0">›</span>
                      <span><strong className="text-white">Platform Evolution:</strong> Users acknowledge that the Platform is in an active state of growth. Specific subscription tiers, credit values, and feature sets are subject to refinement as the ecosystem scales.</span>
                    </li>
                  </ul>
                </section>

                {/* Section 4 */}
                <section>
                  <h3 className="text-base font-bold text-white flex items-center gap-2 mb-3">
                    <span className="text-electric-cyan font-mono text-xs">04.</span> Free Accounts & Branding
                  </h3>
                  <ul className="space-y-3 text-text-secondary text-sm list-none pl-0">
                    <li className="flex gap-2">
                      <span className="text-electric-cyan font-mono shrink-0">›</span>
                      <span><strong className="text-white">Free Tier Usage:</strong> The Platform is free for Artists to join and use for the purpose of building their Fan Circle and initiating Transmissions.</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-electric-cyan font-mono shrink-0">›</span>
                      <span><strong className="text-white">Platform Attribution:</strong> In exchange for the use of the free tools, Artists agree that all electronic Transmissions sent via the Platform will include fans.audio branding and/or non-intrusive advertising provided by the Company in the footer or margins.</span>
                    </li>
                  </ul>
                </section>

                {/* Section 5 */}
                <section>
                  <h3 className="text-base font-bold text-white flex items-center gap-2 mb-3">
                    <span className="text-electric-cyan font-mono text-xs">05.</span> Account Closure & Data Persistence
                  </h3>
                  <ul className="space-y-3 text-text-secondary text-sm list-none pl-0">
                    <li className="flex gap-2">
                      <span className="text-electric-cyan font-mono shrink-0">›</span>
                      <span><strong className="text-white">Artist Exit Strategy:</strong> Should an Artist choose to close their account, their public profile and public-facing Pressings will be removed from the Platform's general discovery areas.</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-electric-cyan font-mono shrink-0">›</span>
                      <span><strong className="text-white">Legacy Access:</strong> To protect the Fan's investment, any Pressings previously unlocked by Fans will remain available to those specific Fans via their private collection. The Artist's content will persist on the Platform's servers solely to fulfill these existing "Access Key" obligations.</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-electric-cyan font-mono shrink-0">›</span>
                      <span><strong className="text-white">Relationship Portability:</strong> Per our Sovereign Data policy, the Company will facilitate the export of the Artist's Fan contact data upon account closure to ensure the Artist maintains their direct audience relationship.</span>
                    </li>
                  </ul>
                </section>

                {/* Section 6 */}
                <section>
                  <h3 className="text-base font-bold text-white flex items-center gap-2 mb-3">
                    <span className="text-electric-cyan font-mono text-xs">06.</span> Legal Contact & Compliance
                  </h3>
                  <ul className="space-y-3 text-text-secondary text-sm list-none pl-0">
                    <li className="flex gap-2">
                      <span className="text-electric-cyan font-mono shrink-0">›</span>
                      <span><strong className="text-white">Governing Law:</strong> This agreement is governed by the laws of England and Wales.</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-electric-cyan font-mono shrink-0">›</span>
                      <span><strong className="text-white">Official Notices:</strong> All legal notices, copyright claims, or data requests must be directed to: admin@bmic.music.</span>
                    </li>
                    <li className="flex gap-2">
                      <span className="text-electric-cyan font-mono shrink-0">›</span>
                      <span><strong className="text-white">Physical Address:</strong> 4 Yorke Street, Hucknall, Nottingham, England, NG15 7BT</span>
                    </li>
                  </ul>
                </section>
              </div>
            </div>

            {/* Footer with Checkbox and Button */}
            <div className="px-6 py-5 border-t border-white/10 space-y-4">
              <label className="flex items-start gap-3 cursor-pointer group">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={(e) => setAgreed(e.target.checked)}
                  className="mt-0.5 w-5 h-5 rounded border-2 border-white/20 bg-surface text-electric-cyan focus:ring-electric-cyan/30 focus:ring-2 cursor-pointer accent-[#00F5FF]"
                />
                <div className="text-sm">
                  <span className="text-white font-medium">I agree to the Terms of Service</span>
                  <p className="text-text-muted text-xs mt-1 leading-relaxed">
                    I understand that fans.audio is a trading style of Boogie Microphone Limited and that by joining, I am entering the Vanguard of an authentic, human-first music ecosystem.
                  </p>
                </div>
              </label>

              <button
                onClick={onAccept}
                disabled={!agreed || submitting}
                className="btn-primary w-full !justify-center disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:shadow-none"
              >
                {submitting ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Creating Profile...
                  </span>
                ) : (
                  'Agree & Create Profile'
                )}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
